import { Dialog, DialogConfig, DialogRef } from '@angular/cdk/dialog';
import { ComponentType } from '@angular/cdk/overlay';
import { Injectable, TemplateRef } from '@angular/core';
import { ConfirmDialogData } from './confirm-dialog.interface';
import { ConfirmDialogComponent } from './confirm-dialog/confirm-dialog';

/**
 * An extension of the Angular CDK Dialog service that provides a common interface for all dialogs.
 */
@Injectable({
    providedIn: 'root',
})
export class LcDialog extends Dialog {
    /**
     * CDK Dialog sets `aria-hidden` on sibling elements of the overlay container
     * before its FocusTrap moves focus into the dialog. This creates a brief
     * window where the triggering element is focused inside an `aria-hidden`
     * subtree, producing a browser accessibility warning. Blurring the active
     * element first moves focus to `<body>`, which is outside the hidden subtree.
     */
    override open<R = unknown, D = unknown, C = unknown>(
        componentOrTemplateRef: ComponentType<C> | TemplateRef<C>,
        config?: DialogConfig<D, DialogRef<R, C>>
    ): DialogRef<R, C> {
        (document.activeElement as HTMLElement | null)?.blur();
        return super.open(componentOrTemplateRef, config);
    }

    /**
     * Opens a dialog specifically for the purpose of confirmations.
     *
     * The dialog will return `true` on confirmation, `false` for a denial, and `undefined` if the dialog is dismissed.
     *
     * usage:
     * ```
     * const dialogRef = service.confirm({data: {questionText: 'confirm?', confirmActionText: 'Yes'}});
     * const userResponse$ = dialogRef.afterClosed().pipe(map(x => !!x));
     * ```
     *
     * Note: If your use case is more complex, define a custom dialog component and use the `LcDialog` service to open it.
     */
    confirm(
        config: DialogConfig<
            ConfirmDialogData,
            DialogRef<boolean | undefined, ConfirmDialogComponent>
        >
    ) {
        return this.open(ConfirmDialogComponent, config);
    }
}
